import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertOctagon, AlertTriangle, Info, CheckCircle2, ArrowRight } from "lucide-react";
import { StatCard, Card, Loading, ErrorState, RangePicker, TrendChart, Pill } from "./AdminUI";

const SEVERITY = {
  critical: { Icon: AlertOctagon, cls: "text-red-600", bg: "bg-red-50 border-red-200", label: "Critical" },
  warning: { Icon: AlertTriangle, cls: "text-amber-600", bg: "bg-amber-50 border-amber-200", label: "Warning" },
  info: { Icon: Info, cls: "text-blue-600", bg: "bg-blue-50 border-blue-200", label: "Info" },
};

export default function Overview({ goto, attention }) {
  const [snap, setSnap] = useState(null);
  const [trends, setTrends] = useState(null);
  const [days, setDays] = useState(() => Number(localStorage.getItem("limya_admin_range")) || 30);
  const [error, setError] = useState(null);

  useEffect(() => {
    setError(null);
    api.get("/admin/overview").then(r => setSnap(r.data))
      .catch(e => setError(e.response?.data?.detail || e.message));
  }, []);

  useEffect(() => {
    localStorage.setItem("limya_admin_range", String(days));
    setTrends(null);
    api.get("/admin/trends", { params: { days } }).then(r => setTrends(r.data)).catch(() => setTrends(null));
  }, [days]);

  const items = attention?.items || [];

  return (
    <div className="space-y-6" data-testid="admin-overview">
      {/* ---------------- NEEDS ATTENTION (always first) ---------------- */}
      <section data-testid="needs-attention">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#222]/60">Needs attention</h2>
          {items.length > 0 && <Pill tone={attention.counts.critical ? "red" : "amber"}>{items.length}</Pill>}
        </div>
        {!attention ? <Loading label="Checking platform state…" /> : items.length === 0 ? (
          <div className="bg-white rounded-2xl p-5 border border-[#2E6B57]/20 flex items-center gap-3"
            data-testid="needs-attention-clear">
            <CheckCircle2 className="text-[#2E6B57]" size={20} />
            <p className="text-sm font-medium">Nothing needs you right now. No stuck jobs, failed payments, unresolved reports or configuration blockers.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(it => {
              const s = SEVERITY[it.severity] || SEVERITY.info;
              return (
                <button key={it.key} onClick={() => goto(it.section, it.tab)}
                  className={`w-full text-left border rounded-2xl px-4 py-3 flex items-start gap-3 hover:shadow-sm transition-shadow ${s.bg}`}
                  data-testid={`attention-item-${it.key}`}>
                  <s.Icon size={18} className={`${s.cls} shrink-0 mt-0.5`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">{it.title}</p>
                    <p className="text-xs text-[#222]/60 mt-0.5">{it.detail}</p>
                  </div>
                  <ArrowRight size={15} className="text-[#222]/35 shrink-0 mt-1" />
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* ---------------- HEALTH SNAPSHOT ---------------- */}
      <section>
        <h2 className="text-sm font-bold uppercase tracking-wider text-[#222]/60 mb-3">Health snapshot</h2>
        {error ? <ErrorState error={error} onRetry={() => api.get("/admin/overview").then(r => { setSnap(r.data); setError(null); })} />
          : !snap ? <Loading /> : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4" data-testid="health-snapshot">
              <StatCard testid="stat-accounts" label="Total accounts"
                value={snap.totals.clients + snap.totals.free_pros + snap.totals.pro_plus}
                sub={`${snap.totals.active_accounts} active`} />
              <StatCard testid="stat-clients" label="Clients" value={snap.totals.clients} />
              <StatCard testid="stat-pros" label="Professionals"
                value={snap.totals.free_pros + snap.totals.pro_plus}
                sub={`${snap.totals.pro_plus} on Pro Plus`} />
              <StatCard testid="stat-new-regs" label="New registrations (30d)" value={snap.totals.new_registrations_30d} />
              <StatCard testid="stat-subs" label="Active subscriptions" value={snap.subscriptions.pro_plus_active}
                sub={snap.subscriptions.canceling ? `${snap.subscriptions.canceling} cancelling` : "Enrollment paused"} />
              <StatCard testid="stat-reports" label="Unresolved reports" value={snap.open_items.moderation_reports}
                tone={snap.open_items.moderation_reports ? "alert" : undefined} />
              <StatCard testid="stat-support" label="Open support items" value={snap.open_items.support_tickets} />
              <StatCard testid="stat-verified" label="Verified Profiles" value={snap.verification.verified}
                sub={`${snap.verification.missing_requirements} incomplete`} />
            </div>
          )}
      </section>

      {/* ---------------- ACTIVITY / TRENDS ---------------- */}
      <section>
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#222]/60">Activity</h2>
          <RangePicker value={days} onChange={setDays} testid="overview-range" />
        </div>
        {!trends ? <Loading label="Aggregating…" /> : (
          <div className="space-y-4">
            <TrendChart testid="chart-registrations" title="New registrations"
              subtitle={`Real signups, bucketed by ${trends.bucket}. Source: users.created_at`}
              data={trends.registrations} bucket={trends.bucket} type="bar" />
            <div className="grid xl:grid-cols-2 gap-4">
              <TrendChart testid="chart-requests" title="Requests created"
                subtitle="Demand side of the marketplace. Source: requests.created_at"
                data={trends.requests_created} bucket={trends.bucket} height={180} />
              <TrendChart testid="chart-posts" title="Posts created"
                subtitle="Wall + profile activity. Source: posts.created_at"
                data={trends.posts_created} bucket={trends.bucket} height={180} color="#C79B6B" />
            </div>
            <Card title="Not tracked yet" testid="overview-honest-gaps">
              <p className="text-xs text-[#222]/60">{trends.notes.active_users}</p>
            </Card>
          </div>
        )}
      </section>

      {/* ---------------- RECENT ADMIN ACTIONS ---------------- */}
      {snap && (
        <Card title="Recent admin actions" testid="overview-recent-actions"
          right={<button onClick={() => goto("system", "audit")} className="text-xs font-semibold text-[#245544]">
            Full audit log →</button>}>
          {snap.recent_actions.length === 0 ? (
            <p className="text-sm text-[#222]/45 py-3">No admin actions logged yet.</p>
          ) : (
            <div className="space-y-1.5">
              {snap.recent_actions.map(a => (
                <div key={a.id} className="text-xs flex items-center justify-between gap-3 border-b border-black/5 pb-1.5 last:border-0">
                  <span className="truncate"><b>{a.action}</b>{a.target_label ? ` — ${a.target_label}` : ""}</span>
                  <span className="text-[#222]/40 shrink-0">{new Date(a.created_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
